import React from 'react';
import { last as _last } from 'lodash';

import BooleanCondition from '../../boolean/boolean-condition';
import MultiSelectCondition from '../../multi-select/multi-select-condition';
import NumericCondition from '../../numeric/numeric-condition';
import FreeTextCondition from '../../free-text/free-text-condition';
import ArrayCondition from '../../array/array-condition';
import type { Condition } from '../rule-utils';
import { ConditionType } from '../rule-utils';
import type { ConditionsTranslations } from '../rule';

interface GenericConditionProps {
	index: number;
	translations: ConditionsTranslations;
	condition: Condition;
	handleChange: (c: Condition, value: unknown) => void;
	removeCondition: (c: Condition) => void;
	disabled?: boolean;
	maxAttributePathItems?: number;
	parentContainerClass?: string;
	withIndexedLabel?: boolean;
}

export const GenericCondition = ({
	index,
	translations,
	condition,
	handleChange,
	removeCondition,
	withIndexedLabel = true,
	parentContainerClass,
	maxAttributePathItems,
	disabled = false
}: GenericConditionProps) => {
	switch (condition.type) {
		case ConditionType.BOOLEAN:
			return (
				<BooleanCondition
					key={condition.id}
					disabled={disabled}
					index={index + 1}
					maxAttributePathItems={maxAttributePathItems}
					condition={condition}
					translations={translations.getBooleanTranslations(_last(condition.name.split('.')))}
					onChange={handleChange}
					onRemove={removeCondition}
					withIndexedLabel={withIndexedLabel}
				/>
			);
		case ConditionType.LIST:
			return (
				<MultiSelectCondition
					key={condition.id}
					disabled={disabled}
					index={index + 1}
					maxAttributePathItems={maxAttributePathItems}
					condition={condition}
					options={condition.options}
					onChange={handleChange}
					onRemove={removeCondition}
					parentContainerClass={parentContainerClass}
					withIndexedLabel={withIndexedLabel}
					removeTooltipText={translations.removeTooltipText}
				/>
			);
		case ConditionType.STRING:
			return (
				<FreeTextCondition
					key={condition.id}
					disabled={disabled}
					index={index + 1}
					maxAttributePathItems={maxAttributePathItems}
					condition={condition}
					translations={translations.freeText}
					parentContainerClass={parentContainerClass}
					onChange={handleChange}
					onRemove={removeCondition}
					withIndexedLabel={withIndexedLabel}
				/>
			);
		case ConditionType.NUMBER:
			return (
				<NumericCondition
					key={condition.id}
					disabled={disabled}
					index={index + 1}
					maxAttributePathItems={maxAttributePathItems}
					condition={condition}
					translations={translations.numeric}
					parentContainerClass={parentContainerClass}
					onChange={handleChange}
					onRemove={removeCondition}
					withIndexedLabel={withIndexedLabel}
				/>
			);

		case ConditionType.ARRAY:
			return (
				<ArrayCondition
					key={condition.id}
					disabled={disabled}
					index={index + 1}
					maxAttributePathItems={maxAttributePathItems}
					condition={condition}
					onRemove={removeCondition}
					onChange={handleChange}
					translations={translations.array}
					removeTooltipText={translations.removeTooltipText}
					options={condition.options}
					withIndexedLabel={withIndexedLabel}
				/>
			);

		default:
			return null;
	}
};
